import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * Hook to persist and restore the current route
 * Prevents losing navigation state when tab becomes inactive or page reloads
 * Only restores route on actual page reloads, not on component remounts
 */
export const useRoutePersistence = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const hasRestoredRef = useRef(false)

  // Save current route to sessionStorage whenever it changes
  useEffect(() => {
    // Don't save auth routes (login, register, etc.)
    const authRoutes = ['/login', '/register', '/onboarding', '/reset-password']
    if (!authRoutes.includes(location.pathname)) {
      sessionStorage.setItem('lastRoute', location.pathname + location.search)
    }
  }, [location])

  // Restore route only on actual page reload (not component remount)
  useEffect(() => {
    // Skip if we've already attempted restoration
    if (hasRestoredRef.current) {
      return
    }

    hasRestoredRef.current = true

    // Check if this is a page reload by checking navigation timing
    const navEntry = performance.getEntriesByType('navigation')[0]
    const isPageReload = navEntry?.type === 'reload' || navEntry?.type === 'back_forward'

    // Get saved route
    const lastRoute = sessionStorage.getItem('lastRoute')
    const isDefaultRoute = location.pathname === '/' || location.pathname === '/dashboard'
    
    // Only restore if:
    // 1. We have a saved route that's different from current
    // 2. The saved route is not a default route
    // 3. Either this is a page reload OR we're on a default route (which suggests a reload happened)
    if (lastRoute && 
        lastRoute !== location.pathname + location.search &&
        lastRoute !== '/dashboard' && 
        lastRoute !== '/') {
      
      // Restore if it's a page reload, or if we're on default route (likely after reload)
      if (isPageReload || isDefaultRoute) {
        // Small delay to ensure React Router is ready
        const timeoutId = setTimeout(() => {
          navigate(lastRoute, { replace: true })
        }, 50)
        
        // Cleanup timeout if component unmounts before navigation
        return () => {
          clearTimeout(timeoutId)
        }
      }
    }
  }, []) // Only run once on mount

  return null
}

